/**
 * StreamWatchdog — bounds every non-terminal stream state.
 *
 * The SDK's chat status can sit in 'submitted'/'streaming' forever when the
 * wire goes quiet — the canonical case is a resumable-stream reattach where
 * the server holds a 200 open and never sends a byte (the 2026-06-11
 * composer wedge). Arm while a stream is active, call progress() on every
 * chunk; if stallMs passes with no progress, onStall fires exactly once so
 * the caller can stop() the stream and free the composer.
 */
export class StreamWatchdog {
  private timer: ReturnType<typeof setInterval> | null = null
  private lastProgress = 0

  constructor(private readonly opts: { stallMs: number; onStall: () => void }) {}

  /** Start (or restart) monitoring with a fresh stall clock. */
  arm(): void {
    this.disarm()
    this.lastProgress = Date.now()
    // Check at ~1/10 of the threshold so the fire is at most ~10% late.
    const checkEvery = Math.max(1_000, Math.floor(this.opts.stallMs / 10))
    this.timer = setInterval(() => {
      if (Date.now() - this.lastProgress >= this.opts.stallMs) {
        this.disarm()
        this.opts.onStall()
      }
    }, checkEvery)
  }

  /** Any sign of life from the stream — resets the stall clock. */
  progress(): void {
    this.lastProgress = Date.now()
  }

  /** Stop monitoring (stream reached a terminal state). */
  disarm(): void {
    if (this.timer !== null) {
      clearInterval(this.timer)
      this.timer = null
    }
  }
}
